import {
  DescriptionOutlined,
  LocalShippingOutlined,
  PersonOutlined,
  Inventory2Outlined,
  DataObjectOutlined,
} from "@mui/icons-material";
import { ORDER_LIST_BASE_URL } from "constants";

// How to add a template:
// - `eventNames`: exact names, or a prefix ending in "*" (e.g. "ORDER_*").
// - `link`: main clickable value shown on the card and panel (optional).
// - `sections`: groups of rows; each field reads the first non-empty `paths`.
//   A field can use `value: (payload, event) => ...` instead of `paths`.
//   `type: "status"` shows the value as a colored pill.
// - `list`: repeated items (e.g. line items) shown in the right column.
// - `autoFields`: if no configured row has data, list the payload's own fields.
// Unmatched events use DEFAULT_TEMPLATE.

// Shared field groups, reused across templates.
const ORDER_NUMBER_PATHS = [
  "notificationData.orderNumber",
  "notificationData.otherData.orderNumber",
  "orderSummaryData.orderNumber",
];

const ORDER_LINK = {
  label: "Order Number",
  paths: ORDER_NUMBER_PATHS,
  href: (orderNumber) => `${ORDER_LIST_BASE_URL}/${orderNumber}`,
};

const ORDER_STATUS_BY_EVENT_NAME = {
  ORDER_CREATED: "CREATED",
  ORDER_CONFIRMATION: "CONFIRMED",
  SHIPMENT_DISPATCH: "DISPATCHED",
  SHIPMENT_DELIVERED: "DELIVERED",
};

const ORDER_FIELDS = [
  {
    label: "Order Date",
    paths: ["notificationData.orderDate", "notificationData.otherData.orderDate"],
  },
  {
    label: "Order Status",
    type: "status",
    value: (payload, event) => ORDER_STATUS_BY_EVENT_NAME[event.eventName],
  },
  {
    label: "Payment Type",
    paths: [
      "notificationData.otherData.orderPaymentType",
      "notificationData.otherData.orderType",
    ],
  },
];

const CUSTOMER_FIELDS = [
  { label: "Customer Name", paths: ["notificationData.customerData.name"] },
  { label: "Customer Email", paths: ["notificationData.customerData.email"] },
  {
    label: "Customer Phone",
    paths: ["notificationData.customerData.phoneNumber"],
  },
  { label: "Customer GSTIN", paths: ["notificationData.customerData.gstIn"] },
];

const SHIPMENT_FIELDS = [
  {
    label: "Shipment No.",
    paths: ["notificationData.otherData.shipmentNumber"],
  },
  {
    label: "Seller Order No.",
    paths: ["notificationData.otherData.sellerOrderNumber"],
  },
  {
    label: "Shipment Status",
    paths: ["notificationData.otherData.shipmentStatus"],
  },
  {
    label: "Seller Order Status",
    paths: ["notificationData.otherData.sellerOrderStatus"],
  },
];

const LINE_ITEMS_LIST = {
  title: "Line Items",
  icon: Inventory2Outlined,
  paths: [
    "notificationData.lineItemList",
    "notificationData.orderSummaryData.lineItemList",
  ],
  primary: "sku",
  secondary: "sellerName",
};

const ORDER_SECTION = {
  title: "Order Details",
  icon: DescriptionOutlined,
  fields: ORDER_FIELDS,
};

const CUSTOMER_SECTION = {
  title: "Customer Details",
  icon: PersonOutlined,
  fields: CUSTOMER_FIELDS,
};

export const EVENT_TEMPLATES = {
  ORDER_CREATED: {
    eventNames: ["ORDER_CREATED"],
    link: ORDER_LINK,
    sections: [
      {
        ...ORDER_SECTION,
        fields: [
          ...ORDER_FIELDS,
          {
            label: "SFDC Opportunity ID",
            paths: ["notificationData.otherData.salesforceOpportunityId"],
          },
        ],
      },
      CUSTOMER_SECTION,
    ],
    list: LINE_ITEMS_LIST,
  },

  SHIPMENT_DISPATCH: {
    eventNames: ["SHIPMENT_DISPATCH"],
    link: ORDER_LINK,
    sections: [
      ORDER_SECTION,
      {
        title: "Shipment Details",
        icon: LocalShippingOutlined,
        fields: [
          ...SHIPMENT_FIELDS,
          {
            label: "Scheduled Dispatch Date",
            paths: ["notificationData.otherData.scheduledDispatchDate"],
          },
          {
            label: "Dispatch Date",
            paths: [
              "notificationData.otherData.dispatchDate",
              "notificationData.otherData.actualDispatchDate",
            ],
          },
        ],
      },
      CUSTOMER_SECTION,
    ],
    list: LINE_ITEMS_LIST,
  },

  SHIPMENT_DELIVERED: {
    eventNames: ["SHIPMENT_DELIVERED"],
    link: ORDER_LINK,
    sections: [
      ORDER_SECTION,
      {
        title: "Shipment Details",
        icon: LocalShippingOutlined,
        fields: [
          ...SHIPMENT_FIELDS,
          {
            label: "Scheduled Delivery Date",
            paths: ["notificationData.otherData.scheduledDeliveryDate"],
          },
          {
            label: "Delivery Date",
            paths: [
              "notificationData.otherData.deliveryDate",
              "notificationData.otherData.actualDeliveryDate",
            ],
          },
        ],
      },
      CUSTOMER_SECTION,
    ],
    list: LINE_ITEMS_LIST,
  },

  // Any other order event (ORDER_CONFIRMATION, ORDER_CANCELLED, ...).
  ORDER_GENERIC: {
    eventNames: ["ORDER_*"],
    link: ORDER_LINK,
    sections: [ORDER_SECTION, CUSTOMER_SECTION],
    list: LINE_ITEMS_LIST,
    autoFields: true,
  },
};

// Used when no template matches the event name.
export const DEFAULT_TEMPLATE = {
  link: ORDER_LINK,
  sections: [ORDER_SECTION, CUSTOMER_SECTION],
  list: LINE_ITEMS_LIST,
  autoFields: true,
};

// Section shown for auto-listed payload fields.
export const AUTO_FIELDS_SECTION = {
  title: "Event Data",
  icon: DataObjectOutlined,
  maxFields: 15,
  maxDepth: 4,
  // Keys containing any of these words are hidden (case-insensitive).
  skipKeys: ["html"],
};
